/**
 * Parse hub.yaml + agents/<slug>.yaml + agents/*.md into HubAsCodePayload.
 * Falls back to wayai.yaml for backward compatibility.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { slugify, resolveHubYamlPath } from './utils.js';
import { resourceTypeToSend, scanResourceFiles } from './resource-files.js';
import { applyTurnAttachments, type AttachableTurn, type BytesByHash } from './eval-attachments.js';
import { applyCallOpeningFiles } from './call-openings.js';
import type { HubAsCodePayload, HubAsCodeResource, HubAsCodeEval, HubAsCodeJourney } from './types.js';

import { expected } from './expected.js';
import { isUnder, readRealFileOrThrow, requireRealHubFolder, requireRealSubdirNoSymlink, symlinkRefusal, workspaceRefusal } from './fs-safety.js';

interface WayaiYaml {
  version?: number;
  hub_id: string;
  hub_environment: string;
  preview_label?: string;
  hub: Record<string, unknown>;
  agents?: Array<Record<string, unknown>>;
  resources?: Array<Record<string, unknown>>;
  states?: Array<Record<string, unknown>>;
  teams?: Array<Record<string, unknown>>;
  connections?: Array<{
    name: string; type: string; service?: string; credential?: string; access_token_credential?: string;
    no_auth?: boolean; base_url?: string; headers?: Record<string, string>; settings?: Record<string, unknown>;
    account_sid?: string; enabled?: boolean; authentication_type?: string; sync_credentials_to_production?: boolean;
  }>;
  automations?: Array<Record<string, unknown>> | null;
  contact_lists?: Array<Record<string, unknown>> | null;
  /** Retired (wayai#4931): each read only to warn that it is no longer pushed. */
  outbound_schedules?: unknown;
  outbound_contacts?: unknown;
  outbound_lists?: unknown;
}

/**
 * Parse a hub folder into a HubAsCodePayload.
 * Reads hub.yaml (or wayai.yaml for backward compat) and resolves agents from
 * agents/<slug>.yaml files (or inline agents array for old format).
 * Resolves agent instructions from companion .md files.
 */
export interface ParseHubFolderOptions {
  /** When true, allow hub_id and hub_environment to be absent (for new hubs not yet created on platform). */
  allowNewHub?: boolean;
  /**
   * Refuse, instead of skipping, a file the parser cannot read as config: an agent file with
   * no `name`, an agent, eval or journey file that is not a YAML object, or a resource file
   * over the size limit. `wayai pull` sets it: a skipped file is local config pull would
   * remove without comparing it (#4586).
   */
  strict?: boolean;
}

/**
 * A file the parser cannot read as config. Skipped — with a warning unless `silent` — or,
 * under `ParseHubFolderOptions.strict`, refused.
 */
type Unreadable = (relPath: string, reason: string, opts?: { silent?: boolean }) => void;

const NOT_A_YAML_OBJECT = 'not a YAML object';

/**
 * A symlinked eval/journey entry the parser would consume — a `.yaml`, or a link to a
 * directory (an eval set) — is refused, not skipped: a skipped one drops out of the pushed
 * desired state and the server deletes it (config-parser parity). Any other link (a README)
 * was never uploaded, so it keeps being ignored.
 */
function refuseConsumedLink(hubFolder: string, dir: string, entry: fs.Dirent): void {
  if (!entry.isSymbolicLink()) return;
  const abs = path.join(dir, entry.name);
  let toDir = false;
  try {
    toDir = fs.statSync(abs).isDirectory();
  } catch {
    // dangling — only a `.yaml` name makes it one the parser would have read
  }
  if (toDir || entry.name.endsWith('.yaml')) {
    throw symlinkRefusal(path.relative(hubFolder, abs), 'file or folder');
  }
}

export function parseHubFolder(hubFolder: string, opts?: ParseHubFolderOptions): HubAsCodePayload {
  // Everything parsed here is uploaded (push/diff/publish): a symlinked hub folder or
  // managed subdir would send the link target's files to the platform.
  requireRealHubFolder(hubFolder, false);
  const yamlPath = resolveHubYamlPath(hubFolder);
  if (!yamlPath) {
    throw expected(`hub.yaml not found in ${hubFolder}`);
  }

  const yamlContent = readRealFileOrThrow(hubFolder, yamlPath) ?? '';
  let config: WayaiYaml;
  try {
    config = yaml.load(yamlContent) as WayaiYaml;
  } catch (err) {
    if (err instanceof yaml.YAMLException) {
      throw expected(`Invalid YAML in ${yamlPath}: ${err.message}`);
    }
    throw err;
  }

  if (!config || typeof config !== 'object') {
    throw expected(`Invalid YAML in ${yamlPath}`);
  }

  if (!opts?.allowNewHub) {
    if (!config.hub_id) {
      throw expected(`Missing hub_id in ${yamlPath}`);
    }

    if (!config.hub_environment) {
      throw expected(`Missing hub_environment in ${yamlPath}`);
    }
  }

  const unreadable: Unreadable = (relPath, reason, unreadableOpts) => {
    if (opts?.strict) throw expected(`${relPath}: ${reason}`);
    if (!unreadableOpts?.silent) console.warn(`  Warning: skipping ${relPath} (${reason})`);
  };

  // Resolve agents: try per-agent YAML files first, fall back to inline agents
  const agentYamlFiles = scanAgentYamlFiles(hubFolder, unreadable);
  const rawAgents = agentYamlFiles.length > 0 ? agentYamlFiles : config.agents;

  // Resolve agent instructions from .md files
  const agents = rawAgents?.map((agent) => {
    const resolved = { ...agent };

    if (typeof resolved.instructions === 'string' && resolved.instructions.endsWith('.md')) {
      // Explicit path: "agents/pilot.md" or "pilot.md" (legacy)
      const instrValue = resolved.instructions as string;
      const instructionsPath = instrValue.startsWith('agents/')
        ? path.join(hubFolder, instrValue)
        : path.join(hubFolder, 'agents', instrValue);
      if (!isUnder(path.join(hubFolder, 'agents'), instructionsPath)) {
        throw workspaceRefusal(
          `Agent instructions path "${instrValue}" (agent "${agent.name}") must stay inside agents/.`,
        );
      }

      const instructions = readRealFileOrThrow(hubFolder, instructionsPath);
      if (instructions !== null) {
        resolved.instructions = instructions;
      } else {
        throw expected(
          `Agent instructions file not found: ${instructionsPath} (referenced by agent "${agent.name}")`
        );
      }
    } else if (resolved.instructions === undefined && typeof agent.name === 'string') {
      // Convention-based: derive path from agent name slug
      const conventionPath = path.join(hubFolder, 'agents', `${slugify(agent.name)}.md`);
      const instructions = readRealFileOrThrow(hubFolder, conventionPath);
      if (instructions !== null) resolved.instructions = instructions;
    }

    return resolved;
  });

  // Resolve resource files from disk
  const resources = parseResources(hubFolder, (config.resources || []) as Array<Record<string, unknown>>, unreadable);

  const payload: HubAsCodePayload = {
    version: config.version || 1,
    hub_id: config.hub_id || '',
    hub_environment: config.hub_environment || 'preview',
    hub: config.hub as HubAsCodePayload['hub'],
  };

  // Read-only metadata, server-owned: set by the backend's replicate-preview /
  // hub-update PATCH (contracts/setup/hubs.ts), never authored here. Only used to
  // disambiguate sibling preview-hub folders via the `--<label>` slug suffix
  // (see getHubFolderSlug in workspace.ts). Present only on preview hubs.
  if (config.preview_label) {
    payload.preview_label = config.preview_label;
  }

  if (agents && agents.length > 0) {
    payload.agents = agents as unknown as HubAsCodePayload['agents'];
    // A voice agent's opening files → content hashes, their bytes in `call_opening_files`.
    applyCallOpeningFiles(hubFolder, payload);
  }

  if (resources.length > 0) {
    payload.resources = resources;
  }

  if (config.states && config.states.length > 0) {
    payload.states = config.states as unknown as HubAsCodePayload['states'];
  }

  if (config.teams && config.teams.length > 0) {
    payload.teams = config.teams as unknown as HubAsCodePayload['teams'];
  }

  // Emit `connections` whenever it's an array — INCLUDING an empty `[]` — so a
  // hub.yaml that removes its last connection block (present-but-empty) deletes
  // it on push. Unlike other entities (which `|| []` on the diff side, treating
  // absent == empty), the connection delete guard distinguishes an ABSENT key
  // (no-op) from a present `[]` (delete all unreferenced); the parser must
  // preserve that. A valueless `connections:` (js-yaml null) is intentionally
  // omitted → treated as absent (safe no-op).
  if (Array.isArray(config.connections)) {
    payload.connections = config.connections;
  }

  // `automations` is forwarded whenever it's an array — INCLUDING `[]`, which deletes every
  // automation on push. An ABSENT key (or a valueless `automations:`) is omitted, which the server
  // reads as "leave the hub's automations as they are" (wayai#4931 D14).
  if (Array.isArray(config.automations)) {
    payload.automations = config.automations as unknown as HubAsCodePayload['automations'];
  }
  // `contact_lists` likewise: `[]` deletes every hub contact list, an absent key leaves them as
  // they are (wayai#4931 D14).
  if (Array.isArray(config.contact_lists)) {
    payload.contact_lists = config.contact_lists as unknown as HubAsCodePayload['contact_lists'];
  }
  // Outbound schedules became automations; the old key is not read, so say so rather than drop it.
  if (config.outbound_schedules !== undefined) {
    console.warn('  Warning: hub.yaml `outbound_schedules:` is no longer read — schedules are now `automations:` (see the wayai skill). Its entries were not pushed.');
  }
  // The old hub contacts became the organization's contact book, which hub.yaml does not hold; the
  // old hub lists are recreated as hub lists (`contact_lists:`, a filter over that book) or org lists.
  if (config.outbound_contacts !== undefined) {
    console.warn("  Warning: hub.yaml `outbound_contacts:` is no longer read — contacts are now the organization's contact book (see the wayai skill). Its entries were not pushed.");
  }
  if (config.outbound_lists !== undefined) {
    console.warn("  Warning: hub.yaml `outbound_lists:` is no longer read — recreate each list as a hub list under `contact_lists:`, a filter over the organization's contact book, or as an organization list (see the wayai skill). Its entries were not pushed.");
  }

  // One shared byte accumulator across every eval + journey turn: a content-addressed
  // attachment shared by N scenarios uploads once (gh #3365).
  const bytesByHash: BytesByHash = new Map();

  const evals = scanEvalYamlFiles(hubFolder, bytesByHash, unreadable);
  if (evals.length > 0) {
    payload.evals = evals;
  }

  const journeys = scanJourneyYamlFiles(hubFolder, bytesByHash, unreadable);
  if (journeys.length > 0) {
    payload.journeys = journeys;
  }

  if (bytesByHash.size > 0) {
    payload.eval_attachment_files = [...bytesByHash.values()];
  }

  return payload;
}

// Scan evals/<slug>.yaml (root, path=null) and evals/<set>/<slug>.yaml (one level deep only — flat-set model).
function scanEvalYamlFiles(hubFolder: string, bytesByHash: BytesByHash, unreadable: Unreadable): HubAsCodeEval[] {
  const evalsDir = path.join(hubFolder, 'evals');
  if (!fs.existsSync(evalsDir)) return [];

  const evals: HubAsCodeEval[] = [];
  const seen = new Set<string>(); // composite key "<name>|<path>" — must be unique

  const topEntries = fs.readdirSync(evalsDir, { withFileTypes: true })
    .sort((a, b) => a.name.localeCompare(b.name));

  for (const entry of topEntries) {
    refuseConsumedLink(hubFolder, evalsDir, entry);
    if (entry.isFile() && entry.name.endsWith('.yaml')) {
      // Root-level scenario (no scenario set)
      collectEval(evals, seen, path.join(evalsDir, entry.name), `evals/${entry.name}`, null, hubFolder, bytesByHash, unreadable);
    } else if (entry.isDirectory()) {
      // Scenario-set subfolder (one level deep only)
      const setName = entry.name;
      const setDir = path.join(evalsDir, setName);
      const setEntries = fs.readdirSync(setDir, { withFileTypes: true })
        .sort((a, b) => a.name.localeCompare(b.name));
      for (const sub of setEntries) {
        refuseConsumedLink(hubFolder, setDir, sub);
        if (sub.isDirectory()) {
          throw expected(
            `Nested scenario sets are not supported: ${path.relative(hubFolder, path.join(setDir, sub.name))}. ` +
              `Move scenarios up to evals/${setName}/.`,
          );
        }
        if (sub.isFile() && sub.name.endsWith('.yaml')) {
          collectEval(evals, seen, path.join(setDir, sub.name), `evals/${setName}/${sub.name}`, setName, hubFolder, bytesByHash, unreadable);
        }
      }
    }
  }

  return evals;
}

function collectEval(
  evals: HubAsCodeEval[],
  seen: Set<string>,
  filePath: string,
  relPath: string,
  setName: string | null,
  hubFolder: string,
  bytesByHash: BytesByHash,
  unreadable: Unreadable,
): void {
  const evalEntry = parseEvalYaml(filePath, relPath, setName, hubFolder, bytesByHash);
  if (!evalEntry) {
    unreadable(relPath, NOT_A_YAML_OBJECT);
    return;
  }
  const key = `${evalEntry.name}|${setName ?? ''}`;
  if (seen.has(key)) {
    const where = setName ? `scenario set "${setName}"` : 'root';
    throw expected(
      `Duplicate eval at ${where}: "${evalEntry.name}" (in ${path.basename(filePath)}). ` +
        `Each (name, scenario set) pair must be unique.`,
    );
  }
  seen.add(key);
  evals.push(evalEntry);
}

// Returns null for a file that is not a YAML object (the caller skips or refuses it); throws expected() on hard validation.
function parseEvalYaml(
  filePath: string,
  relPath: string,
  setName: string | null,
  hubFolder: string,
  bytesByHash: BytesByHash,
): HubAsCodeEval | null {
  const content = fs.readFileSync(filePath, 'utf-8');
  let raw: unknown;
  try {
    raw = yaml.load(content);
  } catch (err) {
    if (err instanceof yaml.YAMLException) {
      throw expected(`Invalid YAML in ${relPath}: ${err.message}`);
    }
    throw err;
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const data = raw as Record<string, unknown>;
  const fileSlug = path.basename(filePath, '.yaml');
  const evalName = typeof data.name === 'string' && data.name.trim().length > 0 ? data.name : fileSlug;

  // Required fields: agent, input, expected
  if (typeof data.agent !== 'string' || data.agent.trim().length === 0) {
    throw expected(`Eval "${evalName}" in ${relPath}: missing required field "agent" (string).`);
  }
  if (!data.input || typeof data.input !== 'object' || Array.isArray(data.input)) {
    throw expected(`Eval "${evalName}" in ${relPath}: missing required field "input" (object).`);
  }
  if (!data.expected || typeof data.expected !== 'object' || Array.isArray(data.expected)) {
    throw expected(`Eval "${evalName}" in ${relPath}: missing required field "expected" (object).`);
  }

  // Resolve on-disk attachment PATHS → wire refs, registering bytes. Mutates a copy of
  // the turn so the emitted eval carries refs, not the author's path strings (gh #3365).
  const input = { ...(data.input as AttachableTurn) };
  applyTurnAttachments(hubFolder, input, `Eval "${evalName}" input`, bytesByHash);

  const evalEntry: HubAsCodeEval = {
    name: evalName,
    agent: data.agent,
    path: setName,
    input: input as HubAsCodeEval['input'],
    expected: data.expected as HubAsCodeEval['expected'],
  };

  if (typeof data.id === 'string') evalEntry.id = data.id;
  if (typeof data.agent_id === 'string') evalEntry.agent_id = data.agent_id;
  if (typeof data.runs === 'number') evalEntry.runs = data.runs;
  if (data.enabled === false) evalEntry.enabled = false;
  if (Array.isArray(data.history)) {
    evalEntry.history = (data.history as AttachableTurn[]).map((turn, i) => {
      const t = { ...turn };
      applyTurnAttachments(hubFolder, t, `Eval "${evalName}" history[${i}]`, bytesByHash);
      return t;
    }) as HubAsCodeEval['history'];
  }
  if (typeof data.evaluator_instructions === 'string') {
    evalEntry.evaluator_instructions = data.evaluator_instructions;
  }
  const variables = parseVariablesMap(data.variables, `Eval "${evalName}" in ${relPath}`);
  if (variables) evalEntry.variables = variables;
  // Eval-session seed fixture (gh #3090).
  const fixture = parseFixtureField(data.fixture, `Eval "${evalName}" in ${relPath}`, 'fixture');
  if (fixture) evalEntry.fixture = fixture;
  const targetBase = parseFixtureField(data.target_base, `Eval "${evalName}" in ${relPath}`, 'target_base');
  if (targetBase) evalEntry.target_base = targetBase;
  // User-scope state seeds (gh #3430).
  const initialState = parseInitialStateField(data.initial_state, `Eval "${evalName}" in ${relPath}`);
  if (initialState) evalEntry.initial_state = initialState;
  // The contact the runs serve as their customer (#5083 RK2); push checks it is a contact id.
  const customer = parseFixtureField(
    data.customer_contact_id, `Eval "${evalName}" in ${relPath}`, 'customer_contact_id', "the customer's contact id",
  );
  if (customer) evalEntry.customer_contact_id = customer;

  return evalEntry;
}

/**
 * Parse an optional `initial_state:` list (gh #3430). Absent/empty → undefined
 * (omitted, parity with the emitter so a hand-authored `initial_state: []` doesn't
 * oscillate on push/pull). Each entry must be `{ slug, scope?: 'user', value: object }`;
 * a malformed entry throws with the caller's `label` so a bad edit surfaces an error
 * rather than silently dropping the field.
 */
function parseInitialStateField(raw: unknown, label: string): HubAsCodeEval['initial_state'] | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (!Array.isArray(raw)) {
    throw expected(`${label}: "initial_state" must be a list of { slug, value } entries.`);
  }
  if (raw.length === 0) return undefined;
  const out: NonNullable<HubAsCodeEval['initial_state']> = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw expected(`${label}: each "initial_state" entry must be an object with "slug" and "value".`);
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.slug !== 'string' || !e.slug.trim()) {
      throw expected(`${label}: an "initial_state" entry is missing a non-empty "slug".`);
    }
    if (e.scope !== undefined && e.scope !== 'user') {
      throw expected(`${label}: "initial_state" entry "${e.slug}" has unsupported scope "${String(e.scope)}" (only "user").`);
    }
    if (!e.value || typeof e.value !== 'object' || Array.isArray(e.value)) {
      throw expected(`${label}: "initial_state" entry "${e.slug}" must have a "value" object.`);
    }
    out.push({ slug: e.slug.trim(), scope: 'user', value: e.value as Record<string, unknown> });
  }
  return out;
}

/**
 * Parse an optional `variables:` map-of-arrays (gh #3007), shared by the eval and
 * journey parsers. Returns the map when present and well-formed, `undefined` when
 * the key is absent (or not an object), and throws with the caller-supplied
 * `label` when it's an object with a non-array value — so a malformed author edit
 * surfaces an error rather than silently dropping the field.
 */
function parseVariablesMap(raw: unknown, label: string): Record<string, unknown[]> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const vars = raw as Record<string, unknown>;
  // An empty map is treated as absent — parity with the server's `hasEvalVariables`
  // and the emitter (which omits empty), so a hand-authored `variables: {}` doesn't
  // produce a phantom `{}`-vs-null diff that oscillates on every push/pull.
  if (Object.keys(vars).length === 0) return undefined;
  if (!Object.values(vars).every((v) => Array.isArray(v))) {
    throw expected(`${label}: "variables" must be a map of arrays (variables.<name>: [ ... ]).`);
  }
  return vars as Record<string, unknown[]>;
}

/**
 * Parse an optional scalar field (`fixture` / `target_base`, gh #3090; a scenario's or
 * journey's `customer_contact_id`, #5083 RK2, with its own `hint`), shared by the eval and journey parsers. Trims; blank/absent → undefined (omitted, parity
 * with the emitter). Throws on a non-string so a malformed edit surfaces an error.
 */
function parseFixtureField(raw: unknown, label: string, key: string, hint = 'the Rekor fixture name'): string | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw !== 'string') {
    throw expected(`${label}: "${key}" must be a string (${hint}).`);
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

// Scan journeys/<slug>.yaml — a FLAT folder (one file per journey). Unlike evals
// there are no scenario-set subfolders: a journey owns its own managed set, which
// is NOT authored. Returns journeys sorted alphabetically by filename.
//
// NOTE on step ids: the backend mints stable `step_id`s on concluding assistant
// turns at create time (`ensureStepIds`) and keys per-step `step_config` overrides
// + derived evals by them. A hand-authored journey pushed WITHOUT step_ids gets
// server-minted ids in the DB but not on disk — so run `wayai pull` after creating
// or first-pushing a journey to sync those ids back (same "pull to sync
// server-assigned ids" discipline as entity `id`s). Re-storing a journey whose
// on-disk transcript lacks the server's step_ids re-mints them, severing
// derived-eval history.
function scanJourneyYamlFiles(hubFolder: string, bytesByHash: BytesByHash, unreadable: Unreadable): HubAsCodeJourney[] {
  const journeysDir = path.join(hubFolder, 'journeys');
  if (!fs.existsSync(journeysDir)) return [];

  const journeys: HubAsCodeJourney[] = [];
  const seen = new Set<string>(); // journey name — must be unique

  const entries = fs.readdirSync(journeysDir, { withFileTypes: true })
    .sort((a, b) => a.name.localeCompare(b.name));

  for (const entry of entries) {
    refuseConsumedLink(hubFolder, journeysDir, entry);
    if (entry.isDirectory()) {
      throw expected(
        `Subfolders are not supported under journeys/: ${path.relative(hubFolder, path.join(journeysDir, entry.name))}. ` +
          `A journey owns its own managed scenario set — put each journey in a single journeys/<slug>.yaml file.`,
      );
    }
    if (!entry.isFile() || !entry.name.endsWith('.yaml')) continue;

    const journeyEntry = parseJourneyYaml(path.join(journeysDir, entry.name), `journeys/${entry.name}`, hubFolder, bytesByHash);
    if (!journeyEntry) {
      unreadable(`journeys/${entry.name}`, NOT_A_YAML_OBJECT);
      continue;
    }
    if (seen.has(journeyEntry.name)) {
      throw expected(
        `Duplicate journey: "${journeyEntry.name}" (in ${entry.name}). Each journey name must be unique.`,
      );
    }
    seen.add(journeyEntry.name);
    journeys.push(journeyEntry);
  }

  return journeys;
}

// Returns null for a file that is not a YAML object (the caller skips or refuses it); throws expected() on hard validation.
function parseJourneyYaml(
  filePath: string,
  relPath: string,
  hubFolder: string,
  bytesByHash: BytesByHash,
): HubAsCodeJourney | null {
  const content = fs.readFileSync(filePath, 'utf-8');
  let raw: unknown;
  try {
    raw = yaml.load(content);
  } catch (err) {
    if (err instanceof yaml.YAMLException) {
      throw expected(`Invalid YAML in ${relPath}: ${err.message}`);
    }
    throw err;
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const data = raw as Record<string, unknown>;
  const fileSlug = path.basename(filePath, '.yaml');
  const journeyName = typeof data.name === 'string' && data.name.trim().length > 0 ? data.name : fileSlug;

  // Required field: agent. transcript may be empty/absent (the backend allows a
  // 0-step journey to create — match eval leniency).
  if (typeof data.agent !== 'string' || data.agent.trim().length === 0) {
    throw expected(`Journey "${journeyName}" in ${relPath}: missing required field "agent" (string).`);
  }
  if (data.transcript !== undefined && !Array.isArray(data.transcript)) {
    throw expected(`Journey "${journeyName}" in ${relPath}: "transcript" must be an array.`);
  }
  if (data.step_config !== undefined && (typeof data.step_config !== 'object' || Array.isArray(data.step_config) || data.step_config === null)) {
    throw expected(`Journey "${journeyName}" in ${relPath}: "step_config" must be an object keyed by step_id.`);
  }

  const journeyEntry: HubAsCodeJourney = {
    name: journeyName,
    agent: data.agent,
  };

  if (typeof data.id === 'string') journeyEntry.id = data.id;
  if (typeof data.agent_id === 'string') journeyEntry.agent_id = data.agent_id;
  if (data.enabled === false) journeyEntry.enabled = false;
  if (typeof data.evaluator_instructions === 'string') {
    journeyEntry.evaluator_instructions = data.evaluator_instructions;
  }
  if (Array.isArray(data.transcript)) {
    journeyEntry.transcript = (data.transcript as AttachableTurn[]).map((turn, i) => {
      const t = { ...turn };
      applyTurnAttachments(hubFolder, t, `Journey "${journeyName}" transcript[${i}]`, bytesByHash);
      return t;
    }) as HubAsCodeJourney['transcript'];
  }
  if (data.step_config && typeof data.step_config === 'object' && !Array.isArray(data.step_config)) {
    journeyEntry.step_config = data.step_config as HubAsCodeJourney['step_config'];
  }
  // Per-run variables (gh #3007): a map of arrays, propagated to every derived step.
  const variables = parseVariablesMap(data.variables, `Journey "${journeyName}" in ${relPath}`);
  if (variables) journeyEntry.variables = variables;
  // The contact every step serves as its customer (#5083 RK2); push checks it is a contact id.
  const customer = parseFixtureField(
    data.customer_contact_id, `Journey "${journeyName}" in ${relPath}`, 'customer_contact_id', "the customer's contact id",
  );
  if (customer) journeyEntry.customer_contact_id = customer;
  // Eval-session seed fixture (gh #3090).
  const fixture = parseFixtureField(data.fixture, `Journey "${journeyName}" in ${relPath}`, 'fixture');
  if (fixture) journeyEntry.fixture = fixture;
  const targetBase = parseFixtureField(data.target_base, `Journey "${journeyName}" in ${relPath}`, 'target_base');
  if (targetBase) journeyEntry.target_base = targetBase;

  return journeyEntry;
}

/**
 * Scan agents/ directory for per-agent YAML config files.
 * Returns agent configs sorted alphabetically by filename.
 * Resolves instructions from companion .md files.
 */
function scanAgentYamlFiles(hubFolder: string, unreadable: Unreadable): Array<Record<string, unknown>> {
  const agentsDir = path.join(hubFolder, 'agents');
  if (!fs.existsSync(agentsDir)) return [];

  const yamlFiles = fs.readdirSync(agentsDir)
    .filter((f) => f.endsWith('.yaml'))
    .sort();

  if (yamlFiles.length === 0) return [];

  const agents: Array<Record<string, unknown>> = [];
  for (const file of yamlFiles) {
    const filePath = path.join(agentsDir, file);
    const content = readRealFileOrThrow(hubFolder, filePath) ?? '';
    let agent: Record<string, unknown>;
    try {
      agent = yaml.load(content) as Record<string, unknown>;
    } catch (err) {
      if (err instanceof yaml.YAMLException) {
        throw expected(`Invalid YAML in agents/${file}: ${err.message}`);
      }
      throw err;
    }
    if (!agent || typeof agent !== 'object') {
      unreadable(`agents/${file}`, NOT_A_YAML_OBJECT, { silent: true });
      continue;
    }
    if (!agent.name) {
      unreadable(`agents/${file}`, "missing 'name' field");
      continue;
    }
    agents.push(agent);
  }

  return agents;
}

/**
 * Scan resource directories and populate file entries with content and hashes.
 */
function parseResources(
  hubFolder: string,
  configResources: Array<Record<string, unknown>>,
  unreadable: Unreadable,
): HubAsCodeResource[] {
  const resourcesDir = path.join(hubFolder, 'resources');
  const results: HubAsCodeResource[] = [];

  for (const res of configResources) {
    const resource: HubAsCodeResource = {
      name: res.name as string,
    };
    if (res.id) resource.id = res.id as string;
    const type = resourceTypeToSend(resource.name, res.type);
    if (type) resource.type = type;
    if (res.description) resource.description = res.description as string;
    if (res.enabled === false) resource.enabled = false;
    if (res.skill_name) resource.skill_name = res.skill_name as string;

    // Scan filesystem for files
    const resSlug = slugify(resource.name);
    const resDir = path.join(resourcesDir, resSlug);
    requireRealSubdirNoSymlink(hubFolder, resDir, false);

    if (fs.existsSync(resDir)) {
      const files = scanResourceFiles(resDir, '', (relPath, reason) => unreadable(`resources/${resSlug}/${relPath}`, reason));
      if (files.length > 0) {
        resource.files = files;
      }
    }

    results.push(resource);
  }

  return results;
}
